import { motion } from "framer-motion";
import {
    HouseLine,
    Buildings,
    BuildingApartment,
    ClipboardText,
    Wrench,
    ChartLineUp,
    ArrowRight,
} from "@phosphor-icons/react";

const services = [
    {
        icon: HouseLine,
        title: "Audit énergétique maison",
        bullets: ["Maison individuelle", "Diagnostic complet", "Rapport sous 48h"],
        accent: "blue",
        tag: "Particuliers",
    },
    {
        icon: BuildingApartment,
        title: "Audit copropriété",
        bullets: ["Diagnostic collectif", "Recommandations travaux", "Accompagnement AG"],
        accent: "green",
        tag: "Copropriétés",
    },
    {
        icon: Buildings,
        title: "Audit immeuble",
        bullets: ["Bâtiments collectifs", "Analyse thermique", "Plan d'action"],
        accent: "blue",
        tag: "Professionnels",
    },
    {
        icon: ClipboardText,
        title: "DPE Collectif",
        bullets: ["Obligation réglementaire", "Étiquette énergie", "Mise en conformité"],
        accent: "amber",
        tag: "Réglementaire",
    },
    {
        icon: Wrench,
        title: "DTG",
        bullets: ["Diagnostic Technique Global", "État du bâti", "Travaux préconisés"],
        accent: "green",
        tag: "Technique",
    },
    {
        icon: ChartLineUp,
        title: "PPPT",
        bullets: ["Plan Pluriannuel de Travaux", "Stratégie 10 ans", "Budgétisation"],
        accent: "blue",
        tag: "Stratégique",
    },
];

const extras = [
    "Analyse thermique avancée",
    "Accompagnement à la rénovation",
    "Études énergétiques sur-mesure",
];

const accentMap = {
    blue: {
        bg: "bg-lumos-blue/10",
        text: "text-lumos-blue",
        ring: "group-hover:ring-lumos-blue/40",
        glow: "from-lumos-blue/20",
    },
    green: {
        bg: "bg-lumos-green/10",
        text: "text-lumos-green",
        ring: "group-hover:ring-lumos-green/40",
        glow: "from-lumos-green/20",
    },
    amber: {
        bg: "bg-lumos-amber/10",
        text: "text-lumos-amber",
        ring: "group-hover:ring-lumos-amber/40",
        glow: "from-lumos-amber/20",
    },
};

export default function Services() {
    return (
        <section id="prestations" data-testid="services-section" className="relative py-24 lg:py-28 bg-lumos-bg">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
                    <div>
                        <span className="eyebrow">Nos prestations</span>
                        <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-lumos-ink leading-[1.05]">
                            Un expert pour chaque<br className="hidden sm:block" />
                            <span className="font-serif-display text-lumos-blue">type de bien.</span>
                        </h2>
                    </div>
                    <p className="text-lumos-muted lg:max-w-md">
                        De la maison individuelle à la copropriété, nous délivrons des diagnostics rapides, fiables et conformes aux dernières réglementations.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {services.map((s, i) => {
                        const Icon = s.icon;
                        const c = accentMap[s.accent];
                        return (
                            <motion.article
                                key={i}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.55, delay: i * 0.05 }}
                                className="group relative bg-white rounded-2xl border border-lumos-border p-7 card-lift overflow-hidden"
                                data-testid={`service-card-${i}`}
                            >
                                <div className={`absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gradient-to-br ${c.glow} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                                <div className="relative flex items-start justify-between mb-5">
                                    <div className={`w-14 h-14 rounded-xl ${c.bg} grid place-items-center ${c.text} group-hover:scale-110 transition-transform duration-500`}>
                                        <Icon size={28} weight="duotone" />
                                    </div>
                                    <span className="text-[10px] tracking-widest uppercase font-bold text-lumos-muted/70">{s.tag}</span>
                                </div>

                                <h3 className="relative font-display text-xl lg:text-2xl font-bold text-lumos-ink mb-4">
                                    {s.title}
                                </h3>

                                <ul className="relative space-y-2 mb-6">
                                    {s.bullets.map((b) => (
                                        <li key={b} className="flex items-center gap-2 text-sm text-lumos-muted">
                                            <span className={`w-1.5 h-1.5 rounded-full ${c.text === 'text-lumos-blue' ? 'bg-lumos-blue' : c.text === 'text-lumos-green' ? 'bg-lumos-green' : 'bg-lumos-amber'}`} />
                                            {b}
                                        </li>
                                    ))}
                                </ul>

                                <a href="#contact" className={`relative inline-flex items-center gap-1.5 text-sm font-semibold ${c.text} group/link`}>
                                    Demander un devis
                                    <ArrowRight size={14} weight="bold" className="transition-transform duration-300 group-hover/link:translate-x-1" />
                                </a>
                            </motion.article>
                        );
                    })}
                </div>

                {/* Extra services band */}
                <div className="mt-10 rounded-2xl bg-lumos-midnight text-white p-7 lg:p-9 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10 overflow-hidden relative">
                    <div className="absolute inset-0 bg-grid-dark opacity-50" />
                    <div className="relative flex-shrink-0">
                        <span className="eyebrow !text-lumos-green">Études énergétiques</span>
                        <div className="mt-2 font-display text-2xl lg:text-3xl font-bold leading-tight">
                            Aller plus loin que le diagnostic.
                        </div>
                    </div>
                    <div className="relative flex flex-wrap gap-3 lg:ml-auto">
                        {extras.map((e) => (
                            <span key={e} className="px-4 py-2 rounded-full bg-white/8 border border-white/15 text-sm font-medium backdrop-blur-md">
                                {e}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
